import { CheckoutStep, CheckoutSteps } from "../global/apps/services/models/checkout.models";
import { formatWithoutRounding } from "../global/apps/mixins/productPrice.mixin";
import { Money } from "../global/apps/services/models/cart.models";
import { DeliveryLocation, DeliveryMethod } from "../global/apps/utils/API/Delivery/deliveryAPISchema";
import { AddPaymentMethodRequestDto, PaymentMethodViewModel } from "../global/apps/utils/API/Payment/paymentAPISchema";
import { formatCurrency } from "../global/apps/utils/currencyHelpers";
import { Ga4Event, Ga4Events } from "../global/apps/services/models/gtm.models";
import { AfterpayMethods } from "./consts";

export default class CheckoutHelpers {
    private static validPaymentImageIds = [
        "riverty_invoice",
        "vipps",
        "nets",
        "riverty_installment",
    ];

    public static qualifiesForFreeShipping(
        freeShippingActive?: boolean,
        freeShippingLimit?: Money,
        subTotal?: Money,
    ): boolean {
        if (!freeShippingActive || !freeShippingLimit || !subTotal) return false;
        return freeShippingActive && freeShippingLimit.amount <= subTotal.amount;
    }

    public static amountNeededToQualifyForFreeShipping(
        freeShippingActive?: boolean,
        freeShippingLimit?: Money,
        value?: Money,
    ): Money | undefined {
        if (
            !freeShippingActive ||
            !value ||
            !freeShippingLimit ||
            freeShippingLimit.amount <= value.amount
        )
            return;

        return {
            amount: freeShippingLimit.amount - value.amount,
            currency: value.currency,
        };
    }

    public static shippingCostFormatted(
        freeShippingActive: boolean,
        freeShippingLimit: Money,
        value: Money,
        selectedShippingMethodPrice: Money,
    ): string {
        if (
            (freeShippingActive && freeShippingLimit.amount <= value.amount) ||
            !selectedShippingMethodPrice.amount
        )
            return "Fri frakt";

        if (selectedShippingMethodPrice?.amount) {
            return `${formatCurrency(selectedShippingMethodPrice.currency)} ${formatWithoutRounding(selectedShippingMethodPrice.amount)}`;
        }

        return "Beregnes senere";
    }

    public static deliveryWindowTimeStamp(date: Date): string {
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}`;
    }

    public static formatDay(date: Date): string {
        if (this.isToday(date)) return "I dag";

        return date.toLocaleDateString("no-NB", {
            weekday: "long",
            day: "numeric",
            month: "long",
        });
    }

    private static isToday(date: Date): boolean {
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    }

    public static formatDeliveryLocationAddress(location: DeliveryLocation): string {
        return `${location.deliveryAddress?.trim()}, ${location.postalCode} ${location.city}`;
    }

    public static logoNameForDeliveryMethod(deliveryMethod: DeliveryMethod): string {
        switch (deliveryMethod.name) {
            case "Porterbuddy":
                return "porterbuddy";
            case "MyPack":
            case "HomeDelivery":
                return "postnord";
            default:
                return "";
        }
    }

    public static logoNameForPaymentMethod(imageId: string): string {
        if (!imageId || !this.validPaymentImageIds.find((id) => id === imageId)) {
            return "";
        }

        return `payment_${imageId}`;
    }

    public static paymentClass(id: string): string {
        const prefix = "checkout-payment__";
        switch (id) {
            case "nets":
                return `${prefix}nets`;
            case "vipps":
                return `${prefix}vipps`;
            case "afterpay_invoice":
                return `${prefix}invoice`;
            case "afterpay_installment":
                return `${prefix}installment`;
            default:
                return "";
        }
    }

    public static hasAfterpayInstallmentInfo(paymentMethod: PaymentMethodViewModel): boolean {
        return !!paymentMethod?.afterPayViewModel?.installmentInfo;
    }

    public static hasAfterpayMethods(paymentMethod: PaymentMethodViewModel): boolean {
        return !!(
            paymentMethod?.afterPayViewModel?.accountInfo ||
            paymentMethod?.afterPayViewModel?.installmentInfo
        );
    }

    public static getFirstAfterpayMethod(paymentMethod: PaymentMethodViewModel): string {
        if (paymentMethod?.afterPayViewModel?.accountInfo)
            return AfterpayMethods.Account;
        if (paymentMethod?.afterPayViewModel?.installmentInfo)
            return AfterpayMethods.Installment;

        return "";
    }

    public static getPaymentMethodBySystemKeyword(
        paymentMethods: PaymentMethodViewModel[],
        selectedSystemKeyword: string,
    ): PaymentMethodViewModel | undefined {
        return paymentMethods.find(
            (method) => method.systemKeyword === selectedSystemKeyword,
        );
    }

    public static getFirstInstallmentValue(paymentMethod: PaymentMethodViewModel): string | undefined {
        if (!paymentMethod) return;

        return paymentMethod?.afterPayViewModel?.installmentInfo
            ?.installmentListItems?.[0]?.value;
    }

    public static getPaymentBody(
        paymentMethod: PaymentMethodViewModel,
        personIdentifier?: string,
        selectedAfterPayInstallmentOption?: string,
        selectedAfterPayInstallmentPlan?: string,
    ): AddPaymentMethodRequestDto {
        const body: AddPaymentMethodRequestDto = {};

        if (paymentMethod.requirePersonIdentifier) {
            body.personIdentifier = personIdentifier;
        }

        if (paymentMethod.isAfterPayInstallment) {
            body.selectedAfterPayInstallmentOption =
                selectedAfterPayInstallmentOption;
        }

        if (selectedAfterPayInstallmentOption === AfterpayMethods.Installment) {
            body.selectedAfterPayInstallmentPlan = selectedAfterPayInstallmentPlan;
        }

        return body;
    }

    public static getSelectedDeliveryMethod(
        deliveryMethods: DeliveryMethod[],
        selectedDeliveryMethodId: string,
    ): DeliveryMethod | undefined {
        return deliveryMethods?.find(
            (method) => method.id === selectedDeliveryMethodId,
        );
    }

    public static setInformationErrors(errors, informationErrors): void {
        this.clearErrors(informationErrors);

        errors.errors.forEach(
            (issue: { path: string | number; message: string }) => {
                informationErrors[issue.path].push(issue.message);
            },
        );
    }

    public static clearErrors(errorObject): void {
        Object.keys(errorObject).forEach((key) => (errorObject[key] = []));
    }

    public static getFirstDeliveryLocation(deliveryMethod: DeliveryMethod): DeliveryLocation | undefined {
        return deliveryMethod?.deliveryLocations?.[0];
    }

    public static getFirstDeliveryWindow(deliveryMethod: DeliveryMethod): any {
        return deliveryMethod?.deliveryWindows?.[0];
    }

    public static hasDeliveryLocations(deliveryMethod: DeliveryMethod): boolean {
        return !!deliveryMethod?.deliveryLocations?.length;
    }

    public static hasDeliveryWindows(deliveryMethod: DeliveryMethod): boolean | undefined {
        return !!deliveryMethod?.deliveryWindows?.length;
    }

    public static sentenceCase(text: string): string {
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }

    public static toGoogleAnalyticsEventName(checkoutStep: CheckoutStep): Ga4Event {
        switch (checkoutStep) {
            case CheckoutSteps.CustomerInformation:
                return Ga4Events.BeginCheckout;
            case CheckoutSteps.Delivery:
                return Ga4Events.AddShippingInfoAddress;
            case CheckoutSteps.Payment:
                return Ga4Events.AddShippingInfo;
            case CheckoutSteps.FinalizeOrder:
                return Ga4Events.AddPaymentInfo;
            case CheckoutSteps.Confirmation:
                return Ga4Events.Purchase;
            default:
                return Ga4Events.NoEvent;
        }
    }
}
